/**
 * Экран «Дом»: Очаг, доход за сутки, перенос (§15, §7.2).
 */

import { formatCrystals, formatDateTime, formatDuration, type LocaleCode } from "@hive/i18n";
import { useTranslation } from "react-i18next";

import type { HearthView } from "../api/hearth.js";
import type { EconomySummaryView } from "../api/wallet.js";
import "./hearth/hearth.css";
import "./money/money.css";
import { Crystals } from "./CrystalIcon.jsx";

export interface HearthScreenProps {
  readonly locale: LocaleCode;
  readonly timeZone: string;
  readonly hearth: HearthView | null;
  readonly hearthLoading?: boolean;
  readonly summary: EconomySummaryView | null;
  readonly onOpenMap: () => void;
  readonly onStartMove: () => void;
}

export function HearthScreen({
  locale,
  timeZone,
  hearth,
  hearthLoading = false,
  summary,
  onOpenMap,
  onStartMove,
}: HearthScreenProps): React.JSX.Element {
  const { t } = useTranslation(["hearth", "common", "economy"]);

  if (hearthLoading) {
    return (
      <div className="screen screen--hearth">
        <p className="screen__empty">{t("common:state.loading")}</p>
      </div>
    );
  }

  if (!hearth) {
    return (
      <div className="screen screen--hearth">
        <header className="screen__head">
          <h1 className="screen__title">{t("hearth:screen.title")}</h1>
        </header>
        {/*
          Иллюстрация Очага обесцвечена фильтром: «здесь будет твой дом».
          Второй файл картинки для серого состояния не нужен.
        */}
        <div className="hearth-empty">
          <img
            className="hearth-empty__art"
            src="/art/hearth.webp"
            alt=""
            width={396}
            height={420}
            decoding="async"
          />
          <p className="hearth-empty__text">{t("hearth:screen.empty")}</p>
          <button type="button" className="button button--primary" onClick={onOpenMap}>
            {t("hearth:screen.open_map")}
          </button>
        </div>
      </div>
    );
  }

  const moveAt = new Date(hearth.moveAvailableAt);
  const moveMsLeft = Math.max(0, moveAt.getTime() - Date.now());
  const canMove = moveMsLeft <= 0;

  return (
    <div className="screen screen--hearth">
      <header className="screen__head">
        <h1 className="screen__title">{t("hearth:screen.title")}</h1>
      </header>

      <section className="hearth-panel" aria-label={t("hearth:screen.title")}>
        <div className="hearth-panel__stage">
          <img
            className="hearth-panel__art"
            src="/art/hearth.webp"
            alt=""
            width={396}
            height={420}
            decoding="async"
          />

          <dl className="hearth-panel__facts">
            <div className="hearth-panel__fact">
              <dt>{t("hearth:hex")}</dt>
              <dd>{hearth.hexId}</dd>
            </div>
            <div className="hearth-panel__fact">
              <dt>{t("hearth:timezone", { tz: hearth.hearthTz })}</dt>
            </div>
            <div className="hearth-panel__fact">
              <dt>
                {t("hearth:placed", {
                  time: formatDateTime(locale, new Date(hearth.placedAt), timeZone),
                })}
              </dt>
            </div>
          </dl>
        </div>

        {canMove ? (
          <button type="button" className="button button--primary" onClick={onStartMove}>
            {t("hearth:move.available")}
          </button>
        ) : (
          <p className="hearth-panel__cooldown">
            {t("hearth:move.cooldown", { duration: formatDuration(locale, moveMsLeft) })}
          </p>
        )}
        <p className="hearth-panel__hint">{t("hearth:move.hint")}</p>
      </section>

      {summary && (
        <section className="hearth-income" aria-label={t("hearth:income.title")}>
          <h2 className="screen__section-title">{t("hearth:income.title")}</h2>
          <dl className="summary__facts">
            <div className="summary__fact summary__fact--in">
              <dt>{t("hearth:income.hearth")}</dt>
              <dd>
                <Crystals>{formatCrystals(locale, summary.incomeHearth * 1000, "hive")}</Crystals>
              </dd>
            </div>
            <div className="summary__fact summary__fact--in">
              <dt>{t("hearth:income.lodgers")}</dt>
              <dd>
                <Crystals>{formatCrystals(locale, summary.incomeLodgers * 1000, "hive")}</Crystals>
              </dd>
            </div>
            <div className="summary__fact summary__fact--in">
              <dt>{t("hearth:income.work")}</dt>
              <dd>
                <Crystals>{formatCrystals(locale, summary.incomeWork * 1000, "hive")}</Crystals>
              </dd>
            </div>
          </dl>
        </section>
      )}
    </div>
  );
}
